/**
 * Global interval and timeout tracker for debugging polling issues
 */

class IntervalTracker {
  constructor() {
    this.intervals = new Map();
    this.timeouts = new Map();
    this.originalSetInterval = window.setInterval;
    this.originalClearInterval = window.clearInterval;
    this.originalSetTimeout = window.setTimeout;
    this.originalClearTimeout = window.clearTimeout;
    
    this.setupInterceptors();
  }

  setupInterceptors() {
    const self = this;
    
    // Intercept setInterval
    window.setInterval = function(callback, delay, ...args) {
      const stack = new Error().stack;
      const intervalId = self.originalSetInterval.call(window, callback, delay, ...args);
      
      self.intervals.set(intervalId, {
        callback: callback.toString().substring(0, 200),
        delay,
        created: new Date(),
        stack: stack.split('\n').slice(1, 6).join('\n'), // First 5 stack frames
        active: true
      });
      
      console.log('🔄 NEW INTERVAL CREATED:', {
        id: intervalId,
        delay,
        callback: callback.toString().substring(0, 100),
        totalActive: self.intervals.size
      });
      
      return intervalId;
    };

    // Intercept clearInterval
    window.clearInterval = function(intervalId) {
      if (self.intervals.has(intervalId)) {
        const info = self.intervals.get(intervalId);
        info.active = false;
        info.cleared = new Date();
        console.log('🛑 INTERVAL CLEARED:', {
          id: intervalId,
          wasActive: info.active,
          duration: info.cleared - info.created
        });
        self.intervals.delete(intervalId);
      }
      return self.originalClearInterval.call(window, intervalId);
    };

    // Intercept setTimeout
    window.setTimeout = function(callback, delay, ...args) {
      const stack = new Error().stack;
      const timeoutId = self.originalSetTimeout.call(window, callback, delay, ...args);
      
      self.timeouts.set(timeoutId, {
        callback: callback.toString().substring(0, 200),
        delay,
        created: new Date(),
        stack: stack.split('\n').slice(1, 6).join('\n'),
        active: true
      });
      
      return timeoutId;
    };

    // Intercept clearTimeout
    window.clearTimeout = function(timeoutId) {
      if (self.timeouts.has(timeoutId)) {
        const info = self.timeouts.get(timeoutId);
        info.active = false;
        info.cleared = new Date();
        self.timeouts.delete(timeoutId);
      }
      return self.originalClearTimeout.call(window, timeoutId);
    };
  }

  getActiveIntervals() {
    const active = [];
    this.intervals.forEach((info, id) => {
      if (info.active) {
        active.push({ id, ...info });
      }
    });
    return active;
  }

  getActiveTimeouts() {
    const active = [];
    this.timeouts.forEach((info, id) => {
      if (info.active) {
        active.push({ id, ...info });
      }
    });
    return active;
  }

  logActiveIntervals() {
    const active = this.getActiveIntervals();
    console.log('🔍 ACTIVE INTERVALS:', active.length);
    active.forEach((interval, index) => {
      console.log(`  ${index + 1}. ID: ${interval.id}, Delay: ${interval.delay}ms`);
      console.log(`     Callback: ${interval.callback.substring(0, 100)}...`);
      console.log(`     Created: ${interval.created.toISOString()}`);
      console.log(`     Stack: ${interval.stack.split('\n')[0]}`);
    });
    return active;
  }

  clearAllIntervals() {
    console.log('🧹 CLEARING ALL INTERVALS');
    const active = this.getActiveIntervals();
    active.forEach(interval => {
      console.log(`Clearing interval ${interval.id}`);
      this.originalClearInterval.call(window, interval.id);
    });
    this.intervals.clear();
  }

  findPollingIntervals() {
    const active = this.getActiveIntervals();
    const polling = active.filter(interval => 
      interval.callback.includes('poll') || 
      interval.callback.includes('fetch') ||
      interval.callback.includes('api') ||
      interval.delay <= 5000 // Short intervals are likely polling
    );
    
    console.log('🎯 POTENTIAL POLLING INTERVALS:', polling.length);
    polling.forEach((interval, index) => {
      console.log(`  ${index + 1}. ID: ${interval.id}, Delay: ${interval.delay}ms`);
      console.log(`     Callback: ${interval.callback}`);
      console.log(`     Stack: ${interval.stack}`);
    });
    
    return polling;
  }
}

// Create global instance
const intervalTracker = new IntervalTracker();

// Expose globally for debugging
window.intervalTracker = intervalTracker;
window.logActiveIntervals = () => intervalTracker.logActiveIntervals();
window.findPollingIntervals = () => intervalTracker.findPollingIntervals();
window.clearAllIntervals = () => intervalTracker.clearAllIntervals();

export default intervalTracker;
